import { currentUserId, getPref, setPref } from "./userPrefs";
// Which surface an issue opens on.
//
// An issue is raised in one of several places and its evidence lives in
// whichever of them: a 2D sheet in Document Control, a screenshot from
// the 3D viewer, a photo or a video from a phone, a model exported
// against it from Revit or Navisworks. The resolve page used to decide
// what to show from a layout heuristic, so an issue raised in 3D could
// open on a blank markup canvas and an issue whose whole story was a
// site video opened on nothing at all.
//
// The rule below reads what the issue actually HAS, in the order that
// answers "what was this issue raised about":
//
//   3d     a model push (Revit / Navisworks) is linked, or the issue was
//          raised in the model viewer and still points at a model
//   sheet  a 2D sheet is attached (docControl / 2d sources)
//   image  a screenshot, markup image or photo
//   video  a video attachment and nothing above it
//   blank  nothing attached yet
//
// The viewer's own choice wins over the rule and is remembered per issue
// (localStorage) — someone reviewing a fix in 3D should not be dropped
// back onto the screenshot every time the page polls.
export const SURFACES = ["sheet", "image", "3d", "video", "document"];

export function issueHasModel(issue) {
  const zip = issue?.sourceModel?.zipInfo || issue?.sourceModelZipInfo;
  if (zip && Array.isArray(zip.paths) && zip.paths.length) return true;
  // A resolution pushed from the plugin points the issue at the
  // project's manifest and nothing else (there is no per-export zip to
  // list) - that is still a streamable model.
  return !!(issue?.sourceModel?.manifestPath || issue?.sourceModel?.path);
}

export function issueHasSheet(issue) {
  return !!(issue?.sourceDocument && issue.sourceDocument.url);
}

export function issueHasImage(issue) {
  return !!(
    issue?.sourceModelViewer?.screenshot ||
    issue?.thumbnail ||
    (issue?.screenshotVersions || []).length ||
    (issue?.attachments || []).some((a) => a && a.kind === "image")
  );
}

// PDFs, spreadsheets and anything else pasted onto the issue that is not
// an image or a video. They open ON the canvas (IssueDocumentPane), which
// is why they are a surface rather than a chip in a strip.
export function issueDocuments(issue) {
  return (issue?.attachments || []).filter((a) => {
    if (!a || !a.url) return false;
    const kind = String(a.kind || "").toLowerCase();
    if (["pdf", "excel", "doc", "document", "drawing", "file"].includes(kind)) return true;
    return /\.(pdf|xlsx?|xlsm|csv|docx?|rtf|txt|dwg|dxf|dwfx?)(\?|$)/i.test(a.url || a.name || "");
  });
}

export function issueVideos(issue) {
  return (issue?.attachments || []).filter(
    (a) => a && (a.kind === "video" || /^video\//i.test(a.mime || ""))
  );
}

// A model push linked to the issue — the plugin resolved against it, so
// the change is the thing to look at.
export function issueHasModelPush(issue) {
  return !!(
    issue?.pendingResolution?.commitId ||
    issue?.resolution?.commitId ||
    issue?.linkedCommitId
  );
}

// Raised on a sheet AND with a picture pasted at creation: the picture is
// what the person wanted looked at (the sheet is where it belongs), so
// the canvas opens on the media, fitted, and the sheet is one click away
// in the media history. A 3D snapshot carries a viewport state and is
// not a paste; an issue drawn in Document Control has no viewer
// screenshot at all.
export function issueRaisedWithPastedMedia(issue) {
  const shot = issue?.sourceModelViewer?.screenshot;
  if (!shot || issue?.sourceModelViewer?.viewportState) return false;
  return ["2d", "hybrid", "docControl"].includes(String(issue?.sourceType || ""));
}

export function defaultSurfaceFor(issue) {
  if (!issue) return "image";
  const model = issueHasModel(issue);
  if (model && (issueHasModelPush(issue) || issue.sourceType === "modelViewer" || issue.sourceType === "3d")) {
    return "3d";
  }
  if (issueHasSheet(issue) && issueRaisedWithPastedMedia(issue)) return "image";
  if (issueHasSheet(issue)) return "sheet";
  if (issueHasImage(issue)) return "image";
  if (issueVideos(issue).length) return "video";
  if (issueDocuments(issue).length) return "document";
  if (model) return "3d";
  return "image"; // the markup canvas, which also carries the paste hint
}

// Surfaces worth offering for this issue, in tab order. 3D is offered
// whenever a model is attached; the others only when they have content,
// except the image canvas which is always available (it is where a
// pasted screenshot lands).
export function availableSurfaces(issue) {
  const out = [];
  if (issueHasSheet(issue)) out.push("sheet");
  out.push("image");
  if (issueHasModel(issue)) out.push("3d");
  if (issueVideos(issue).length) out.push("video");
  if (issueDocuments(issue).length) out.push("document");
  return out;
}

const KEY = (id) => `bidlight_issue_surface_${id}`;
// Remembered per person (helpers/userPrefs, owner 2026-09-18: "by user"):
// one small map of issue -> surface, newest 300 kept. The old per-browser
// key is still read, so a surface picked before the move is not lost.
const PREF = "issues.surface";
const MAX = 300;

export function rememberSurface(issueId, surface) {
  if (!issueId || !surface) return;
  const uid = currentUserId();
  const map = { ...((uid && getPref(uid, PREF)) || {}) };
  delete map[issueId];
  map[issueId] = surface;
  const keys = Object.keys(map);
  keys.slice(0, Math.max(0, keys.length - MAX)).forEach((k) => { delete map[k]; });
  if (uid) setPref(uid, PREF, map);
  else {
    try { localStorage.setItem(KEY(issueId), surface); } catch (e) { /* private mode - the rule still applies */ }
  }
}

export function recallSurface(issueId) {
  if (!issueId) return "";
  const uid = currentUserId();
  const map = (uid && getPref(uid, PREF)) || {};
  if (map[issueId]) return map[issueId];
  try {
    return localStorage.getItem(KEY(issueId)) || "";
  } catch (e) {
    return "";
  }
}
